import { useContext, useState } from "react";
import { UserContext } from "../../contexts/userContext";
import { DELETE, MARKETPLACE, POST } from "../../utils/constants";
import { IListFeesContainerProps } from "../../interfaces/configuration";
import { fetchData } from "../../utils/dataProvider";
import { TEntity } from "../../interfaces/userContext";
import ListFeesView from "./ListFeesView";
import { useDebounce } from "../../customHooks/useDebounce";

const getFeesTableColumns = (entity: TEntity) => [
  { label: "Nombre" },
  { label: "Precio total de la orden" },
  { label: "Precio total del envío" },
  { label: "Volumetría" },
  ...(entity === MARKETPLACE ? [{ label: "Merchants" }] : []),
  { label: "Operadores Logísticos" },
  { label: "Tarifa plana" },
  { label: "Estado" },
  { label: "Acciones" },
];

const ListFeesContainer = ({
  fees,
  setFees,
  setAlert,
  setSelectedFee,
  handleGetFees,
  page,
  handlePageChange,
  count,
}: IListFeesContainerProps) => {
  const {
    getMerchantNameFromId,
    currentUser: { entity },
    carriers: carrierList,
  } = useContext(UserContext);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [selectedFeeId, setSelectedFeeId] = useState<string | null>(null);
  const columns = getFeesTableColumns(entity);

  const getMerchantListFromArrayOfIds = (merchants: number[]): string =>
    merchants.map((id: number) => getMerchantNameFromId(id)).join(", ");

  const handleDeleteFee = async () => {
    try {
      await fetchData({
        url: `/customization_fees/${selectedFeeId}`,
        method: DELETE,
      });
      handleGetFees();
    } catch (error) {
      console.error(error);
    }
  };

  const { setSubmitting: setSubmittingDeleteFee } =
    useDebounce(handleDeleteFee);

  const deleteFee = () => setSubmittingDeleteFee(true);

  const handleOpenConfirmationModal = (id: string) => {
    setSelectedFeeId(id);
    setOpenConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(false);
    setSelectedFeeId(null);
  };

  const handleToggleFee = async (id: string, enabled: boolean) => {
    const targetFee = fees.find(({ id: feeId }) => feeId === id);
    const feesWithUpdatedTargetFee = fees.map((fee) =>
      fee.id === targetFee?.id ? { ...fee, enabled } : fee
    );
    // Optimistic update regarding of promise result.
    setFees(feesWithUpdatedTargetFee);
    try {
      await fetchData({
        url: "/customization_fees",
        method: POST,
        body: { ...targetFee, id, enabled },
      });
      handleGetFees();
    } catch (error: any) {
      setAlert({
        showAlert: true,
        typeOfAlert: "error",
        message: error?.message || error,
      });
      console.error(error);
      // In case of error, revert optimistic update to match current fee state.
      setFees(fees);
    }
  };

  const viewProps = {
    entity,
    fees,
    setSelectedFee,
    page,
    handlePageChange,
    count,
    carrierList,
    columns,
    getMerchantListFromArrayOfIds,
    handleOpenConfirmationModal,
    handleToggleFee,
    openConfirmationModal,
    handleCloseConfirmationModal,
    deleteFee,
  };

  return <ListFeesView {...viewProps} />;
};

export default ListFeesContainer;
